import { Injectable } from '@angular/core';
import { AlertController, Platform } from 'ionic-angular';

/**
 * Native
 */
import { Camera, CameraOptions } from '@ionic-native/camera';
import { Diagnostic } from '@ionic-native/diagnostic';
import { File } from '@ionic-native/file';
import { Geolocation } from '@ionic-native/geolocation';
import { AndroidPermissionsServiceProvider } from '../android-permissions/android-permissions.service';

declare const navigator;
/*
  Generated class for the AvatarProvider provider.

  See https://angular.io/docs/ts/latest/guide/dependency-injection.html
  for more info on providers and Angular 2 DI.
*/

@Injectable()
export class PermissionCheckProvider {

  constructor(
    public platform: Platform,
    public geolocation: Geolocation,
    public diagnostic: Diagnostic,
    public camera: Camera,
    public androidPermissionsServiceProvider: AndroidPermissionsServiceProvider,
    public alertCtrl: AlertController,
    public file: File,
  ) {

  }

  checkPermissionLocation() {
    return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION);
  }

  checkPermissionBackgroundLocation(): Promise<{ hasPermission: boolean }> {
    const ACCESS_BACKGROUND_LOCATION = 'android.permission.ACCESS_BACKGROUND_LOCATION';
    return this.androidPermissionsServiceProvider.checkPermission(ACCESS_BACKGROUND_LOCATION);
  }

  requestPermissionBackgroundLocation(): Promise<{ hasPermission: boolean }> {
    const ACCESS_BACKGROUND_LOCATION = 'android.permission.ACCESS_BACKGROUND_LOCATION';
    return this.androidPermissionsServiceProvider.requestPermissions([ACCESS_BACKGROUND_LOCATION]);
  }

  checkPermissionStorageAllFiles() {
    return new Promise<boolean>((resolve, reject) => {
      navigator.Env.isExternalStorageManager((result) => {
        if (result == 'false') {
          resolve(false);
        } else {
          resolve(true);
        }
      }, error => {
        reject(error);
      })
    })
  }

  requestPermissionLocation() {
    const data = [
      this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_BACKGROUND_LOCATION
    ];
    return this.androidPermissionsServiceProvider.requestPermissions(data);
  }

  getGeolocation(): Promise<any> {
    return this.diagnostic.isLocationEnabled().then((enabled) => {
      console.log("isLocationEnabled", enabled);
      if (enabled) {
        if (this.platform.is('android')) {
          return this.diagnostic.getLocationMode()
            .then(val => {
              console.log("getLocationMode", val);
              if (val === 'high_accuracy') {
                return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION)
                  .then(success => {
                    if (success.hasPermission) {
                      return this.geolocation.getCurrentPosition({
                        maximumAge: (5 * 60) * 1000,
                        timeout: 5000,
                        enableHighAccuracy: true
                      })
                    } else {
                      const data = [
                        this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_COARSE_LOCATION,
                        this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_FINE_LOCATION,
                        this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.ACCESS_LOCATION_EXTRA_COMMANDS
                      ];
                      return this.androidPermissionsServiceProvider.requestPermissions(data).then(rest => {
                        return this.geolocation.getCurrentPosition({
                          maximumAge: (5 * 60) * 1000,
                          timeout: 5000,
                          enableHighAccuracy: true
                        })
                      })
                    }
                  })
              } else {
                return Promise.reject({
                  code: 99,
                  message: 'Please use high accuracy GPS'
                })
              }
            })
        } else {
          return this.geolocation.getCurrentPosition({
            maximumAge: (5 * 60) * 1000,
            timeout: 5000,
            enableHighAccuracy: true
          })
        }
      } else {
        return Promise.reject({
          code: 99,
          message: 'To continue, let your devices turn on location'
        })
      }
    }).catch(error => {
      return Promise.reject(error);
    });
  }

  getCameraPermission(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.CAMERA)
        .then(success => {
          if (!success.hasPermission) {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.CAMERA)
          };
        })
    } else {
      return Promise.resolve();
    }
  }

  getCamera(data): Promise<any> {
    return this.getCameraPermission().then(success => {

      const options: CameraOptions = {
        quality: data.quality,
        destinationType: this.camera.DestinationType.FILE_URI,
        sourceType: data.sourceType ? this.camera.PictureSourceType.CAMERA : this.camera.PictureSourceType.PHOTOLIBRARY,
        allowEdit: data.allowEdit,
        encodingType: this.camera.EncodingType.JPEG,
        targetWidth: data.targetWidth,
        targetHeight: data.targetHeight,
        saveToPhotoAlbum: false,
        cameraDirection: data.cameraDirection,
        correctOrientation: true
      }

      return this.camera.getPicture(options)
    })
  }

  getCheckDirectory(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
        success => {
          if (success.hasPermission) {
            return this.file.checkDir(this.file.externalRootDirectory, 'SunFishGO').catch(err => {
              return this.file.createDir(this.file.externalRootDirectory, 'SunFishGO', true)
            })
          } else {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(rest => {
              return this.file.checkDir(this.file.externalRootDirectory, 'SunFishGO').catch(err => {
                return this.file.createDir(this.file.externalRootDirectory, 'SunFishGO', true)
              })
            })
          }
        });
    } else if (this.platform.is('ios')) {
      return this.file.checkDir(this.file.documentsDirectory, 'SunFishGO').catch(err => {
        return this.file.createDir(this.file.documentsDirectory, 'SunFishGO', true)
      })
    } else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  getCheckDirectoryAttendance(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
        success => {
          if (success.hasPermission) {
            return this.file.checkDir(this.file.dataDirectory, 'Attendance').catch(err => {
              return this.file.createDir(this.file.dataDirectory, 'Attendance', true)
            })
          } else {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(rest => {
              return this.file.checkDir(this.file.dataDirectory, 'Attendance').catch(err => {
                return this.file.createDir(this.file.dataDirectory, 'Attendance', true)
              })
            })
          }
        });
    } else if (this.platform.is('ios')) {
      return this.file.checkDir(this.file.dataDirectory, 'Attendance').catch(err => {
        return this.file.createDir(this.file.dataDirectory, 'Attendance', true)
      })
    } else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  getCheckDirectoryActivityRecord(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
        success => {
          if (success.hasPermission) {
            return this.file.checkDir(this.file.dataDirectory, 'ActivityRecord').catch(err => {
              return this.file.createDir(this.file.dataDirectory, 'ActivityRecord', true)
            })
          } else {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(rest => {
              return this.file.checkDir(this.file.dataDirectory, 'ActivityRecord').catch(err => {
                return this.file.createDir(this.file.dataDirectory, 'ActivityRecord', true)
              })
            })
          }
        });
    } else if (this.platform.is('ios')) {
      return this.file.checkDir(this.file.documentsDirectory, 'ActivityRecord').catch(err => {
        return this.file.createDir(this.file.documentsDirectory, 'ActivityRecord', true)
      })
    } else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  setPathDownload(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(
        success => {
          if (success.hasPermission) {
            return this.file.checkDir(this.file.externalRootDirectory, 'Download/GreatDayHr').catch(err => {
              return this.file.createDir(this.file.externalRootDirectory, 'Download/GreatDayHr', true)
            })
          } else {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE).then(rest => {
              return this.file.checkDir(this.file.externalRootDirectory, 'Download/GreatDayHr').catch(err => {
                return this.file.createDir(this.file.externalRootDirectory, 'Download/GreatDayHr', true)
              })
            })
          }
        });
    } else if (this.platform.is('ios')) {
      return this.checkPathIOS();
    } else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  async setNewPathDownload() {
    if (this.platform.is('android')) {
      try {
        const hasPermission = await this.checkPermission();
        if (hasPermission) {
          // check & create directory Download
          await this.checkAndCreateDirectory('Download');
          // check & create directory Download/GreatDayHr
          await this.checkAndCreateDirectory('Download/GreatDayHr');
          return true;
        } else {
          return false;
        }
      } catch (error) {
        return false;
      }
    } else if (this.platform.is('ios')) {
      return this.checkPathIOS();
    } else {
      throw { message: 'Error Platform..' };
    }
  }

  async checkPermission() {
    try {
      const permission = await this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE);
      if (permission.hasPermission) {
        return true;
      } else {
        try {
          await this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.WRITE_EXTERNAL_STORAGE);
          return true;
        } catch (error) {
          return false;
        }
      }
    } catch (error) {
      return false;
    }
  }

  async checkAndCreateDirectory(dir: string) {
    try {
      // jika directory sudah ada
      await this.checkDirectory(dir);
    } catch (error) {
      // jika directory belum ada maka create directory
      await this.createDirectory(dir);
    }
  }

  async createDirectory(dir: string) {
    try {
      await this.file.createDir(this.file.externalRootDirectory, dir, true);
      console.log(`TCL: succces create dir  ${dir}`);
      return true;
    } catch (error) {
      console.log(`TCL: error createDirectory  ${dir}:`, error);
      return false;
    }
  }

  async checkDirectory(dir: string) {
    try {
      await this.file.checkDir(this.file.externalRootDirectory, dir);
      console.log(`TCL: succces check dir exits ${dir}`);
    } catch (error) {
      console.log(`TCL: check error dir  ${dir}`, error);
      throw error;
    }
  }

  async checkPathIOS() {
    try {
      await this.file.checkDir(this.file.documentsDirectory, 'Download').catch(async () => {
        await this.file.createDir(this.file.documentsDirectory, 'Download', true);
      });
      await this.file.checkDir(this.file.documentsDirectory + 'Download/', 'GreatDayHr').catch(async () => {
        await this.file.createDir(this.file.documentsDirectory + 'Download/', 'GreatDayHr', true);
      });
      return true;
    } catch (error) {
      throw new Error(error);
    }
  }

  getPathUrl(): Promise<any> {
    return this.getCheckDirectory()
      .then(val => {
        return Promise.resolve({
          nativeURL: (this.platform.is('android')) ? this.file.externalRootDirectory + 'SunFishGO/' : this.file.documentsDirectory + 'SunFishGO/'
        });
      })
      .catch(error => {
        return Promise.reject(error);
      })
  }

  getPathAttendance(): Promise<any> {
    return this.getCheckDirectoryAttendance()
      .then(val => {
        return Promise.resolve({
          nativeURL: (this.platform.is('android')) ? this.file.dataDirectory + 'Attendance/' : this.file.dataDirectory + 'Attendance/'
        });
      })
      .catch(error => {
        return Promise.reject(error);
      })
  }

  getPathActivityRecord(): Promise<any> {
    return this.getCheckDirectoryActivityRecord()
      .then(val => {
        return Promise.resolve({
          nativeURL: (this.platform.is('android')) ? this.file.dataDirectory + 'ActivityRecord/' : this.file.dataDirectory + 'ActivityRecord/'
        });
      })
      .catch(error => {
        return Promise.reject(error);
      })
  }

  removeDirectory(): Promise<any> {
    if (this.platform.is('android')) {
      return this.file.removeRecursively(this.file.externalRootDirectory, 'SunFishGO')
    } else if (this.platform.is('ios')) {
      return this.file.removeRecursively(this.file.documentsDirectory, 'SunFishGO')
    } else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  removeDirectoryAttendance(): Promise<any> {
    if (this.platform.is('cordova')) {
      return this.file.removeRecursively(this.file.dataDirectory, 'Attendance')
    }
    else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  removeDirectoryActivityRecord(): Promise<any> {
    if (this.platform.is('cordova')) {
      return this.file.removeRecursively(this.file.dataDirectory, 'ActivityRecord')
    }
    else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  checkExistsDirectory(dirName: string, dirPath: string = this.file.dataDirectory): Promise<any> {
    if (this.platform.is('cordova')) {
      return this.file.checkDir(dirPath, dirName);
    }
    else {
      return Promise.reject({
        message: 'Error Platform..'
      });
    }
  }

  getAudioPermission(): Promise<any> {
    if (this.platform.is('android')) {
      return this.androidPermissionsServiceProvider.checkPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.RECORD_AUDIO)
        .then(success => {
          if (!success.hasPermission) {
            return this.androidPermissionsServiceProvider.requestPermission(this.androidPermissionsServiceProvider.androidPermissions.PERMISSION.RECORD_AUDIO)
          };
        })
    } else {
      return Promise.resolve();
    }
  }
}


// WEBPACK FOOTER //
// ./src/providers/permission-check/permission-check.ts